import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import swal from "sweetalert";
import api from "../../../api";
import PageTitle from "../../layouts/PageTitle";

const emptyForm = () => ({
 code: "",
 name: "",
 description: "",
 status: "Active",
});

const normalizeCode = (value = "") =>
 String(value)
 .trim()
 .toUpperCase()
 .replace(/[^A-Z0-9]+/g, "_")
 .replace(/^_+|_+$/g, "");

const ActivityMaster = () => {
 const [list, setList] = useState([]);
 const [filtered, setFiltered] = useState([]);
 const [loading, setLoading] = useState(false);
 const [saving, setSaving] = useState(false);
 const [showModal, setShowModal] = useState(false);
 const [editId, setEditId] = useState(null);
 const [search, setSearch] = useState("");
 const [form, setForm] = useState(emptyForm());

 const fetchList = async () => {
 setLoading(true);
 try {
 const res = await api.get("/activity-masters?includeInactive=true");
 if (res.data.success) {
 setList(res.data.data || []);
 setFiltered(res.data.data || []);
 }
 } catch (error) {
 swal(
 "Error",
 error.response?.data?.error ||
 error.response?.data?.message ||
 error.message ||
 "Failed to load activities",
 "error",
 );
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 fetchList();
 }, []);

 useEffect(() => {
 if (!search.trim()) {
 setFiltered(list);
 return;
 }

 const value = search.toLowerCase();
 setFiltered(
 list.filter(
 (item) =>
 item.name?.toLowerCase().includes(value) ||
 item.code?.toLowerCase().includes(value),
 ),
 );
 }, [search, list]);

 const openAdd = () => {
 setEditId(null);
 setForm(emptyForm());
 setShowModal(true);
 };

 const openEdit = (item) => {
 setEditId(item._id);
 setForm({
 code: item.code || "",
 name: item.name || "",
 description: item.description || "",
 status: item.status || "Active",
 });
 setShowModal(true);
 };

 const buildPayload = () => ({
 code: normalizeCode(form.code || form.name),
 name: form.name,
 description: form.description,
 status: form.status,
 });

 const handleSave = async () => {
 if (!form.name.trim()) {
 return swal("Validation", "Name is required", "warning");
 }

 try {
 setSaving(true);
 const payload = buildPayload();

 if (editId) {
 await api.put(`/activity-masters/${editId}`, payload);
 } else {
 await api.post("/activity-masters", payload);
 }

 swal("Success", "Activity saved successfully", "success");
 setShowModal(false);
 fetchList();
 } catch (error) {
 swal(
 "Error",
 error.response?.data?.message || "Failed to save activity",
 "error",
 );
 } finally {
 setSaving(false);
 }
 };

 const deactivate = (item) => {
 swal({
 title: `Deactivate "${item.name}"?`,
 text: "This activity will stop appearing in DPR dropdown.",
 icon: "warning",
 buttons: true,
 dangerMode: true,
 }).then(async (ok) => {
 if (!ok) return;
 try {
 await api.delete(`/activity-masters/${item._id}`);
 fetchList();
 } catch (error) {
 swal(
 "Error",
 error.response?.data?.message || "Cannot deactivate this activity",
 "error",
 );
 }
 });
 };

 const activeCount = useMemo(
 () => list.filter((item) => item.status === "Active").length,
 [list],
 );

 return (
 <Fragment>
 <PageTitle activeMenu="Activity Master" motherMenu="Masters" />
 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title>Activity Master</Card.Title>
 <Button onClick={openAdd}>+ Add Activity</Button>
 </Card.Header>
 <Card.Body>
 <div className="d-flex justify-content-between align-items-center mb-3">
 <div>
 <Badge bg="success" className="me-2">Active: {activeCount}</Badge>
 <Badge bg="secondary">Total: {list.length}</Badge>
 </div>
 <div style={{ minWidth: "280px" }}>
 <Form.Control
 placeholder="Search by code or name..."
 value={search}
 onChange={(e) => setSearch(e.target.value)}
 />
 </div>
 </div>

 {loading ? (
 <div className="text-center py-5">
 <Spinner animation="border" />
 </div>
 ) : (
 <Table bordered hover responsive className="align-middle">
 <thead className="table-light">
 <tr>
 <th style={{ textAlign: "center" }}>Code</th>
 <th style={{ textAlign: "center" }}>Name</th>
 <th style={{ textAlign: "center" }}>Description</th>
 <th style={{ textAlign: "center" }}>Status</th>
 <th width="180" style={{ textAlign: "center" }}>Actions</th>
 </tr>
 </thead>
 <tbody>
 {filtered.map((item) => (
 <tr key={item._id}>
 <td style={{ textAlign: "center" }}>{item.code}</td>
 <td style={{ textAlign: "center" }}>{item.name}</td>
 <td style={{ textAlign: "center" }}>{item.description || "-"}</td>
 <td style={{ textAlign: "center" }}>
 <Badge bg={item.status === "Active" ? "success" : "secondary"}>
 {item.status}
 </Badge>
 </td>
 <td>
 <div className="d-flex justify-content-center">
 <Button size="sm" variant="outline-primary" className="me-2" onClick={() => openEdit(item)}>
 Edit
 </Button>
 <Button
 size="sm"
 variant="outline-danger"
 onClick={() => deactivate(item)}
 disabled={item.status !== "Active"}
 >
 Deactivate
 </Button>
 </div>
 </td>
 </tr>
 ))}
 {!filtered.length && (
 <tr>
 <td colSpan={5} className="text-center text-muted py-4">
 No activities found
 </td>
 </tr>
 )}
 </tbody>
 </Table>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 <Modal show={showModal} onHide={() => !saving && setShowModal(false)} centered>
 <Modal.Header closeButton={!saving}>
 <Modal.Title>{editId ? "Edit Activity" : "Add Activity"}</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 <Form.Group className="mb-3">
 <Form.Label>Code</Form.Label>
 <Form.Control
 value={form.code}
 placeholder="Auto from name if blank"
 onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
 />
 </Form.Group>
 <Form.Group className="mb-3">
 <Form.Label>Name *</Form.Label>
 <Form.Control
 value={form.name}
 onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
 />
 </Form.Group>
 <Form.Group className="mb-3">
 <Form.Label>Description</Form.Label>
 <Form.Control
 as="textarea"
 rows={3}
 value={form.description}
 onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
 />
 </Form.Group>
 <Form.Group>
 <Form.Label>Status</Form.Label>
 <Form.Select
 value={form.status}
 onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
 >
 <option value="Active">Active</option>
 <option value="Inactive">Inactive</option>
 </Form.Select>
 </Form.Group>
 </Modal.Body>
 <Modal.Footer>
 <Button variant="secondary" onClick={() => setShowModal(false)} disabled={saving}>
 Cancel
 </Button>
 <Button onClick={handleSave} disabled={saving}>
 {saving ? "Saving..." : "Save"}
 </Button>
 </Modal.Footer>
 </Modal>
 </Fragment>
 );
};

export default ActivityMaster;
